import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Alert from "../../components/common/Alert";
import { useLoginWithPinMutation } from "../../store/api";
import { setCredentials } from "../../store/authSlice";

const DEFAULT_DEVICE_ID =
  process.env.REACT_APP_DEVICE_ID || "3fa85f64-5717-4562-b3fc-2c963f66afa6";
const DEFAULT_DEVICE_KEY = process.env.REACT_APP_DEVICE_KEY || "string";

const getOrCreateDeviceIdentity = () => {
  return {
    deviceId: DEFAULT_DEVICE_ID,
    deviceKey: DEFAULT_DEVICE_KEY,
  };
};

const decodeJwtPayload = (token) => {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const normalized =
      payload + "=".repeat((4 - (payload.length % 4 || 4)) % 4);
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
};

const normalizePermissions = (permissions) => {
  if (!permissions) return [];
  if (Array.isArray(permissions)) return permissions.filter(Boolean);
  if (typeof permissions === "string") {
    try {
      const parsed = JSON.parse(permissions);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // ignore
    }
    return permissions.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const getSafeErrorMessage = (error, fallbackMessage) => {
  const candidate =
    error?.data?.detail ||
    error?.data?.message ||
    (typeof error?.error === "string" ? error.error : null) ||
    error?.message;

  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    return trimmed || fallbackMessage;
  }

  return fallbackMessage;
};

const Login = () => {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [showPin, setShowPin] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [loginWithPin, { isLoading }] = useLoginWithPinMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginError, setLoginError] = useState(null);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    // Focus first input on mount
    inputRefs[0].current?.focus();
  }, []);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setLoginError(null);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits are entered
    if (index === 3 && value) {
      const fullPin = [...newPin.slice(0, 3), value].join("");
      if (fullPin.length === 4) {
        handleSubmit(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle arrow keys
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    if (e.key === "ArrowRight" && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d{4}$/.test(pastedData)) {
      const newPin = pastedData.split("");
      setPin(newPin);
      inputRefs[3].current?.focus();
      handleSubmit(pastedData);
    }
  };

  const handleNumpadClick = (num) => {
    const emptyIndex = pin.findIndex((digit) => digit === "");
    if (emptyIndex !== -1) {
      handlePinChange(emptyIndex, num.toString());
    }
  };

  const handleBackspace = () => {
    const lastFilledIndex = pin.findIndex((digit) => digit === "") - 1;
    const indexToDelete = lastFilledIndex === -2 ? 3 : lastFilledIndex;
    if (indexToDelete >= 0) {
      const newPin = [...pin];
      newPin[indexToDelete] = "";
      setPin(newPin);
      inputRefs[indexToDelete].current?.focus();
    }
  };

  const handleSubmit = async (pinValue = null) => {
    const fullPin = pinValue || pin.join("");
    if (fullPin.length !== 4) return;

    setLoginError(null);

    try {
      const { deviceId, deviceKey } = getOrCreateDeviceIdentity();
      const result = await loginWithPin({
        pin: fullPin,
        deviceId,
        deviceKey,
      }).unwrap();
      const token = result?.token;
      if (!token) {
        throw new Error("Login response did not include a token.");
      }
      const claims = decodeJwtPayload(token);
      const permissions = normalizePermissions(
        result?.permissions ?? claims?.permissions,
      );
      const user = {
        userId: result?.userId,
        accessLevel: result?.accessLevel,
        storeId: claims?.storeId || null,
        permissions,
      };
      dispatch(setCredentials({ token, user }));
      navigate("/pos");
    } catch (error) {
      const message = getSafeErrorMessage(
        error,
        (error?.status === 401
          ? "Invalid PIN. Please try again."
          : "Login failed. Please check device setup or try again."),
      );
      setLoginError(message);
      // Trigger shake animation
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      // Clear PIN
      setPin(["", "", "", ""]);
      inputRefs[0].current?.focus();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] dark:bg-slate-900 px-4 py-6 transition-colors duration-200 flex items-center justify-center">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <h1 className="text-center text-xl font-semibold text-slate-900 dark:text-slate-100">
            Edge POS Admin
          </h1>
          <p className="mt-1 text-center text-xs text-slate-600 dark:text-slate-400">
            Enter your PIN to access the admin portal
          </p>

          <form
            className="mt-5 flex flex-col gap-3"
            onSubmit={handleFormSubmit}
          >
            {/* PIN Input Boxes */}
            <div className="flex flex-col items-center gap-2">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                PIN Code
              </label>
              <div
                className={`flex gap-2 ${shakeError ? "animate-shake" : ""}`}
              >
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type={showPin ? "text" : "password"}
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg
                      ${digit ? "border-blue-500 dark:border-blue-400" : "border-slate-300 dark:border-slate-600"}
                      ${loginError ? "border-red-500 dark:border-red-400" : ""}
                      bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                      focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                      transition-all duration-200`}
                    disabled={isLoading}
                  />
                ))}
              </div>

              {/* Show/Hide Toggle */}
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
              >
                {showPin ? (
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                    Hide PIN
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    Show PIN
                  </span>
                )}
              </button>
            </div>

            {/* Error Alert with Animation */}
            {loginError && (
              <div className="animate-slideDown -mt-1">
                <Alert tone="error">{loginError}</Alert>
              </div>
            )}

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto w-full mt-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleNumpadClick(num)}
                  disabled={isLoading}
                  className="h-12 text-base font-semibold rounded-lg border-2 border-slate-300 dark:border-slate-600
                    bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                    hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors duration-150"
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={handleBackspace}
                disabled={isLoading}
                aria-label="Clear PIN digit"
                className="h-12 text-base font-semibold rounded-lg border-2 border-slate-300 dark:border-slate-600
                  bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                  hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => handleNumpadClick(0)}
                disabled={isLoading}
                className="h-12 text-base font-semibold rounded-lg border-2 border-slate-300 dark:border-slate-600
                  bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                  hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150"
              >
                0
              </button>
              <button
                type="submit"
                disabled={isLoading || pin.join("").length !== 4}
                aria-label="Login with PIN"
                className="h-12 text-base font-semibold rounded-lg border-2 border-blue-500 dark:border-blue-400
                  bg-blue-500 dark:bg-blue-600 text-white
                  hover:bg-blue-600 dark:hover:bg-blue-700 active:bg-blue-700 dark:active:bg-blue-800
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150 flex items-center justify-center"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  "Login"
                )}
              </button>
            </div>

            {/* Forgot PIN Link */}
            <div className="text-center -mt-1">
              <button
                type="button"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
                onClick={() =>
                  alert("Please contact your administrator to reset your PIN")
                }
              >
                Forgot PIN?
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
